import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Box,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { IN, LOW_HIGH, NOT_IN } from "../constant";

const LineOfBusinessModal = ({
  open,
  onClose,
  mode,
  selectedItem,
  setSelectedItem,
  handleSave,
  matchingItems,
  lobMappingType,
  lobConfigOptions,
  handleSecondarySwitchChange,
}) => {
  const [manualSecondary, setManualSecondary] = useState(null); // null = follow derived
  const derivedSecondary =
    !!selectedItem?.secondaryType?.trim() ||
    !!selectedItem?.secondaryConfigValue?.trim();
  const showSecondary = manualSecondary ?? derivedSecondary;

  const [hasError, setHasError] = useState(false);
  const [has2ndError, setHas2ndError] = useState(false);
  const [hasPrimaryError, setHasPrimaryError] = useState(false);
  const [hasSecondaryError, setHasSecondaryError] = useState(false);
  // Primary validation: checks if primary values are valid
  const isPrimaryValid =
    !hasError &&
    !!selectedItem?.lobDisplayName?.trim() &&
    !!selectedItem?.primaryType?.trim() &&
    !!selectedItem?.primaryFormatText?.trim() &&
    (selectedItem?.primaryFormatText?.toLowerCase() !== LOW_HIGH
      ? !!selectedItem?.primaryConfigValue
      : !!selectedItem?.primaryConfigValue &&
        !!selectedItem?.primaryConfig2ndValue);

  // Secondary validation: checks if secondary values are valid (if showSecondary is true)
  const isSecondaryValid = showSecondary
    ? !has2ndError &&
      !!selectedItem?.secondaryType?.trim() &&
      !!selectedItem?.secondaryFormatText?.trim() &&
      (selectedItem?.secondaryFormatText?.toLowerCase() !== LOW_HIGH
        ? !!selectedItem?.secondaryConfigValue
        : !!selectedItem?.secondaryConfigValue &&
          !!selectedItem?.secondaryConfig2ndValue)
    : true; // Skip secondary validation if showSecondary is false

  // Save button is enabled only if both primary and secondary fields are valid
  const isSaveEnabled =
    isPrimaryValid &&
    isSecondaryValid &&
    !hasError &&
    !hasPrimaryError &&
    !hasSecondaryError;

  const handleBackdropClick = (event, reason) => {
    if (reason === "backdropClick") return;
    setManualSecondary(null);
    onClose();
  };

  const handleToggleSecondary = (event) => {
    const checked = event.target.checked;
    setManualSecondary(checked);
    handleSecondarySwitchChange(checked); // Call the callback prop with the new value
    if (!checked) {
      setSelectedItem((prev) => ({
        ...prev,
        secondaryType: "",
        secondaryConfigValue: "",
        secondaryConfig2ndValue: "",
      }));
    }
  };

  const handleHighLowChange = (inputValue, fieldKey, setError, primary) => {
    const currentFormat = primary
      ? selectedItem?.primaryFormatText
      : selectedItem?.secondaryFormatText;

    if (currentFormat?.toLowerCase() === LOW_HIGH) {
      const isValid = /^[a-zA-Z0-9]*$/.test(inputValue);
      setError(!isValid);
      if (!isValid) {
        return; // Ignore invalid input
      }
    }

    if (
      currentFormat?.toLowerCase() === IN ||
      currentFormat?.toLowerCase() === NOT_IN
    ) {
      setSelectedItem({
        ...selectedItem,
        [fieldKey]: inputValue,
      });
      setError(!inputValue.includes(",") && inputValue.length > 0);
    } else {
      if (!inputValue.includes(",") && !/\s/.test(inputValue)) {
        setSelectedItem({
          ...selectedItem,
          [fieldKey]: inputValue,
        });
        setError(false);
      } else {
        setError(true); // Set error for invalid input
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleBackdropClick} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {mode === "view"
          ? "Active Lines of Business"
          : "Line of Business Configuration"}
      </DialogTitle>
      <DialogContent>
        {mode === "view" ? (
          <>
            <List
              dense
              sx={{
                border: "1px solid #ccc",
              }}
            >
              {matchingItems.map((lob) => (
                <ListItem key={lob.lineOfBusinessKey}>
                  <ListItemText
                    primary={`${lob.hpLineOfBusiness} - ${lob.lineOfBusinessCode}`}
                  />
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          <>
            <TextField
              label="Display Name"
              fullWidth
              size="small"
              margin="normal"
              value={selectedItem ? selectedItem.lobDisplayName : ""}
              onChange={(e) =>
                setSelectedItem({
                  ...selectedItem,
                  lobDisplayName: e.target.value,
                })
              }
              disabled={mode === "view"}
            />
            <FormControl fullWidth disabled={mode === "view"} sx={{ mb: 2 }}>
              <InputLabel id="primary-type-select-label">
                Primary Line of Business Type
              </InputLabel>
              <Select
                labelId="primary-type-select-label"
                value={selectedItem?.primaryType}
                onChange={(e) => {
                  const selectedOption = lobMappingType.find(
                    (option) => option.value === e.target.value
                  );
                  setSelectedItem({
                    ...selectedItem,
                    primaryType: selectedOption.value,
                  });
                }}
                label="Primary Line of Business Type"
                size="small"
              >
                {lobMappingType.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.value}
                    disabled={option.value === selectedItem?.secondaryType}
                  >
                    {option.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={mode === "view"}>
              <InputLabel id="primary-format-select-label">
                Primary Line of Business Format
              </InputLabel>
              <Select
                labelId="primary-format-select-label" // Connects the Select to the InputLabel
                value={selectedItem?.primaryFormatText || ""}
                onChange={(e) => {
                  const selectedOption = lobConfigOptions.find(
                    (option) => option.value === e.target.value
                  );
                  setSelectedItem({
                    ...selectedItem,
                    primaryFormatText: selectedOption.value, // Store both id and value
                    primaryConfigValue: "",
                    primaryConfig2ndValue: "",
                  });
                }}
                label="Primary Line of Business Format"
                size="small"
              >
                {lobConfigOptions.map((option) => (
                  <MenuItem key={option.id} value={option.value}>
                    {option.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={
                  selectedItem?.primaryFormatText?.toLowerCase() === LOW_HIGH
                    ? "Low Value"
                    : selectedItem?.primaryFormatText || "Primary Config Value"
                }
                fullWidth
                margin="normal"
                size="small"
                value={selectedItem ? selectedItem.primaryConfigValue : ""}
                onChange={(e) => {
                  handleHighLowChange(
                    e.target.value,
                    "primaryConfigValue",
                    setHasError,
                    true
                  );
                }}
                disabled={mode === "view"}
                error={hasError}
                helperText={
                  selectedItem?.primaryFormatText?.toLowerCase() === IN ||
                  selectedItem?.primaryFormatText?.toLowerCase() === NOT_IN
                    ? "Enter comma-separated values."
                    : "Enter a single value without spaces or commas."
                }
              />
              <TextField
                label={
                  selectedItem?.primaryFormatText?.toLowerCase() === LOW_HIGH
                    ? "High Value"
                    : "[2nd LOB Value, if appropriate]"
                }
                fullWidth
                margin="normal"
                size="small"
                value={selectedItem?.primaryConfig2ndValue || ""}
                onChange={(e) => {
                  handleHighLowChange(
                    e.target.value,
                    "primaryConfig2ndValue",
                    setHasPrimaryError
                  );
                }}
                error={hasPrimaryError}
                disabled={
                  mode === "view" ||
                  selectedItem?.primaryFormatText?.toLowerCase() !== LOW_HIGH
                }
                helperText={
                  selectedItem?.primaryFormatText?.toLowerCase() === LOW_HIGH
                    ? "Enter a single value without spaces or commas."
                    : "Enter 2nd LOB Value, if applicable."
                }
              />
            </Box>
            {/* Secondary LOB Mapping Section */}
            <FormControlLabel
              control={
                <Switch
                  checked={showSecondary}
                  onChange={handleToggleSecondary}
                  disabled={mode === "view"}
                />
              }
              label="Secondary LOB Mapping required"
            />

            {showSecondary && (
              <>
                <FormControl
                  fullWidth
                  disabled={mode === "view"}
                  sx={{ mb: 2 }}
                >
                  <InputLabel id="secondary-type-select-label">
                    Secondary Line of Business Type
                  </InputLabel>
                  <Select
                    labelId="secondary-type-select-label"
                    value={selectedItem?.secondaryType || ""}
                    onChange={(e) => {
                      const selectedOption = lobMappingType.find(
                        (option) => option.value === e.target.value
                      );
                      setSelectedItem({
                        ...selectedItem,
                        secondaryType: selectedOption.value,
                      });
                    }}
                    label="Secondary Line of Business Type"
                    size="small"
                  >
                    {lobMappingType.map((option) => (
                      <MenuItem
                        key={option.id}
                        value={option.value}
                        disabled={option.value === selectedItem?.primaryType}
                      >
                        {option.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth disabled={mode === "view"}>
                  <InputLabel id="secondary-format-select-label">
                    Secondary Line of Business Format
                  </InputLabel>
                  <Select
                    labelId="secondary-format-select-label"
                    value={selectedItem?.secondaryFormatText || ""}
                    onChange={(e) => {
                      const selectedOption = lobConfigOptions.find(
                        (option) => option.value === e.target.value
                      );
                      setSelectedItem({
                        ...selectedItem,
                        secondaryFormatText: selectedOption.value,
                        secondaryConfigValue: "",
                        secondaryConfig2ndValue: "",
                      });
                    }}
                    label="Secondary Line of Business Format"
                    size="small"
                  >
                    {lobConfigOptions.map((option) => (
                      <MenuItem key={option.id} value={option.value}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label={
                      selectedItem?.secondaryFormatText?.toLowerCase() ===
                      LOW_HIGH
                        ? "Low Value"
                        : selectedItem?.secondaryFormatText ||
                          "Secondary Config Value"
                    }
                    fullWidth
                    margin="normal"
                    size="small"
                    value={selectedItem?.secondaryConfigValue || ""}
                    onChange={(e) => {
                      handleHighLowChange(
                        e.target.value,
                        "secondaryConfigValue",
                        setHas2ndError,
                        false
                      );
                    }}
                    disabled={mode === "view"}
                    error={has2ndError}
                    helperText={
                      selectedItem?.secondaryFormatText?.toLowerCase() === IN ||
                      selectedItem?.secondaryFormatText?.toLowerCase() ===
                        NOT_IN
                        ? "Enter comma-separated values."
                        : "Enter a single value without spaces or commas."
                    }
                  />
                  <TextField
                    label={
                      selectedItem?.secondaryFormatText?.toLowerCase() ===
                      LOW_HIGH
                        ? "High Value"
                        : "[2nd LOB Value, if appropriate]"
                    }
                    fullWidth
                    margin="normal"
                    size="small"
                    value={selectedItem?.secondaryConfig2ndValue || ""}
                    onChange={(e) => {
                      handleHighLowChange(
                        e.target.value,
                        "secondaryConfig2ndValue",
                        setHasSecondaryError,
                        false
                      );
                    }}
                    error={hasSecondaryError}
                    disabled={
                      mode === "view" ||
                      selectedItem?.secondaryFormatText?.toLowerCase() !==
                        LOW_HIGH
                    }
                    helperText={
                      selectedItem?.secondaryFormatText?.toLowerCase() ===
                      LOW_HIGH
                        ? "Enter a single value without spaces or commas."
                        : "Enter 2nd LOB Value, if applicable."
                    }
                  />
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" sx={{ fontWeight: "bold" }}>
          Close
        </Button>
        {mode !== "view" && (
          <Button
            onClick={handleSave}
            color="primary"
            sx={{ fontWeight: "bold" }}
            disabled={!isSaveEnabled}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
export default LineOfBusinessModal;
